import React, { useCallback, useEffect, useMemo, useState } from 'react';

import SocialMediaTab from '../page/SocialMedia';
import GameStoreTab from '../page/GameStore';
import NewMediaTab from '../page/NewMedia';
import BubbleCommon, {
  feedsNewsTipsText,
  highPriorityPCTipsText,
  shortGoogleText,
  videoHeadText,
} from '@/pages/opinion/components/BubbleCommon';
import { getLocale, useIntl, useLocation } from 'umi';
import {
  privateSourceGameIds,
  specialChannelMap,
  specialParamsMap,
} from '../constants';
import feedsSocialMedia from '@op/operationLog/pages/feedsSocialMedia';
import feedsGameStore from '@op/operationLog/pages/feedsGameStore';
import feedsNewsMedia from '@op/operationLog/pages/feedsNewsMedia';
import { getOpinionFilterCond } from '@/pages/opinion/services';
import ChannelLabel from '@/pages/opinion/components/ChannelLabel';
import { getFilterCondStorage } from '@op/components/FilterCommon/utils/storage';
import { DBO_GAME, DBO_U } from '@op/utils/format';
import { useFilterTopic } from '@op/hooks/useFilterTopic';
import useHasMenu from '@op/hooks/useHasMenu';
import useLanguage from '@op/hooks/useLanguage';

/**
 * 从 URL 参数中检查是否启用 Category 模式
 */
function hasCategoryModeEnabled(search: string): boolean {
  const urlParams = DBO_U.query(search);
  return urlParams.get('_hasCategoryTab') === 'true';
}

export default function useTabs({ gameInfo, wholeGameInfo, tabId }: any) {
  const { edition_unified_id, entity_type } = gameInfo;
  const isHighPriority = DBO_GAME.isHigh(wholeGameInfo);
  const specialChannel = specialChannelMap[edition_unified_id] || null;
  const specialParams = specialParamsMap[edition_unified_id] || {};
  const locale = getLocale();
  const { formatMessage } = useLanguage();
  const location = useLocation();
  const [hasPower, setHasPower] = useState(false);
  const [hasSteam, setHasSteam] = useState(false);
  const [loading, setLoading] = useState(false);

  const { hasMenu } = useHasMenu('Steam Performance', { gameType: 'pc' });

  // 检查 URL 参数是否启用 category 模式
  const enableCategoryMode = useMemo(
    () => hasCategoryModeEnabled(location.search),
    [location.search],
  );

  // 当启用 category 模式时，自动获取 category 数据
  const { data: categoryData, loading: categoryLoading } = useFilterTopic(
    gameInfo,
    {
      autoFetch: enableCategoryMode,
      defaultLoading: enableCategoryMode,
    },
  );

  // 是否为私有源游戏
  const isPrivateSource = useMemo(
    () => privateSourceGameIds.includes(edition_unified_id),
    [edition_unified_id],
  );

  // 构建 SocialMedia 的 filterDefaultConfig
  const socialMediaFilterConfig = useMemo(() => {
    const config: Record<string, any> = {};

    // 私有源游戏配置
    if (isPrivateSource) {
      config.monitor = {
        isDiscordPrivate: false,
        isNightingale: true,
      };
    }

    // category 模式配置（从 URL 参数获取，支持从其他页面跳转时启用）
    if (enableCategoryMode && categoryData) {
      config.topic = {
        hasCategoryTab: true,
        categoryOptions: categoryData,
      };
    }

    return config;
  }, [isPrivateSource, enableCategoryMode, categoryData]);

  const setOpinionFilter = useCallback(
    ({
      isPrivate,
      isMobile,
      data,
    }: {
      isPrivate: boolean;
      isMobile: boolean;
      data: any;
    }) => {
      if (isPrivate) {
        setHasPower(
          data?.monitor_by_account?.monitory?.['discord private']?.length > 0,
        );
      }
      if (!isMobile) {
        setHasSteam(data?.channel?.comments?.includes('steam'));
      }
    },
    [],
  );

  const requestGetData = useCallback(
    ({ isPrivate, isMobile }: { isPrivate: boolean; isMobile: boolean }) => {
      const requestType = [];
      if (isPrivate) requestType.push('monitor_by_account');
      if (!isMobile) requestType.push('channel');

      setLoading(true);
      getOpinionFilterCond({
        ...gameInfo,
        request_type: requestType,
        un_request_type: ['topic_trans'],
      })
        .then((data: any) => {
          setOpinionFilter({ isMobile, isPrivate, data });
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    },
    [gameInfo, setOpinionFilter],
  );

  const storageGetData = useCallback(
    ({ isPrivate, isMobile }: { isPrivate: boolean; isMobile: boolean }) => {
      const storageData = getFilterCondStorage(gameInfo);
      const privateFlag = isPrivate ? storageData?.monitor_by_account : true;
      const notMobileFlag = !isMobile ? storageData?.channel : true;

      if (notMobileFlag && privateFlag) {
        setOpinionFilter({ isMobile, isPrivate, data: storageData });
        return true;
      }
      return false;
    },
    [gameInfo, setOpinionFilter],
  );

  useEffect(() => {
    const isMobile = entity_type === 'mobile';
    if (isPrivateSource || !isMobile) {
      const flag = storageGetData({ isMobile, isPrivate: isPrivateSource });
      if (flag) return;
      if (edition_unified_id && entity_type) {
        requestGetData({ isPrivate: isPrivateSource, isMobile });
      }
    }
  }, [
    entity_type,
    isPrivateSource,
    edition_unified_id,
    storageGetData,
    requestGetData,
  ]);

  const tabs = useMemo(() => {
    // 加载状态处理
    if (loading && tabId === 'gameStore') return [];
    if (categoryLoading) return [];

    const localTabs = [
      {
        id: 'socialMedia',
        label: formatMessage({ id: 'Social Media' }),
        component: (
          <SocialMediaTab
            locale={locale}
            specialParams={specialParams}
            showAigcBtn={true}
            buriedPointInfo={feedsSocialMedia}
            filterDefaultConfig={socialMediaFilterConfig}
          />
        ),
        titleOperation: (
          <div className="game-detail-header-operation">
            {formatMessage({ id: 'Data Update' })}
            <BubbleCommon tipsMap={videoHeadText} />
          </div>
        ),
      },
      {
        id: 'gameStore',
        label:
          entity_type === 'pc' && hasMenu ? (
            <ChannelLabel
              iconStyle={{ width: 16, height: 16 }}
              channel_name="steam"
              replaceText={formatMessage({ id: 'Game Store' })}
            />
          ) : (
            formatMessage({ id: 'Game Store' })
          ),
        component: (
          <GameStoreTab
            locale={locale}
            showAigcBtn={true}
            specialParams={specialParams}
            buriedPointInfo={feedsGameStore}
            hasSteam={hasSteam}
          />
        ),
        titleOperation:
          entity_type === 'mobile' ? (
            <div className="game-detail-header-operation">
              {formatMessage({
                id: 'Google Play reviews identify countries by language',
              })}
              <BubbleCommon tipsMap={shortGoogleText} />
            </div>
          ) : (
            isHighPriority && (
              <div className="game-detail-header-operation">
                {formatMessage({ id: 'Data Update' })}
                <BubbleCommon tipsMap={highPriorityPCTipsText} />
              </div>
            )
          ),
      },
      {
        id: 'newMedia',
        label: formatMessage({ id: 'News Media' }),
        component: (
          <NewMediaTab
            locale={locale}
            showAigcBtn={false}
            buriedPointInfo={feedsNewsMedia}
          />
        ),
        titleOperation: (
          <div className="game-detail-header-operation">
            {formatMessage({ id: 'News Crawl Strategy' })}
            <BubbleCommon tipsMap={feedsNewsTipsText} />
          </div>
        ),
      },
    ];

    // 特殊渠道处理
    const isCommonGame = specialChannel && !isPrivateSource;
    const isPrivateSourceGame = isPrivateSource && hasPower;
    if (isCommonGame || isPrivateSourceGame) {
      const {
        tabValue,
        tabText,
        pageName,
        specialParams: spParams,
        buriedPointInfo,
        filterDefaultValue,
        filterDefaultConfig,
        hasEndElement,
        showAigcBtn = false,
      } = specialChannel;
      localTabs.push({
        id: tabValue,
        label: formatMessage({ id: tabText }),
        component: (
          <SocialMediaTab
            locale={locale}
            pageName={pageName}
            specialParams={spParams}
            hasEndElement={hasEndElement}
            showAigcBtn={showAigcBtn}
            buriedPointInfo={buriedPointInfo}
            filterDefaultValue={filterDefaultValue}
            filterDefaultConfig={filterDefaultConfig}
            isSpecialChannel={true}
            tabText={tabText}
          />
        ),
        titleOperation: <></>,
      });
    }

    return localTabs;
  }, [
    // 加载状态
    loading,
    categoryLoading,
    tabId,
    // 动态配置
    socialMediaFilterConfig,
    // 游戏相关动态值
    entity_type,
    hasSteam,
    hasPower,
    hasMenu,
    // 特殊渠道相关
    specialChannel,
    isPrivateSource,
    formatMessage
  ]);

  return { tabs };
}
